import React, { useEffect, useState, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { cloneDeep, isArray, isEmpty, isNull, toLower } from "lodash";
import uniqid from 'uniqid';
import useApi from '../../../hooks/useApi';
import { Row, Col, Input, Select, Modal, Form, Grid, Switch, notification, Button, Radio, Space } from 'antd';
import {
  getInvoiceTemplate,
  updateIvoiceTemplate
} from '../../../api';
import LoadingSpinner from '../../../components/loading';
import AdminContent from '../../../components/adminContent';
import FloatingContainer from '../../../components/floatingContainer'
import InvoicePaymentTimePicker from '../../../components/invoicePaymentTimePicker'
import InvoicePaymentReminderModal from '../../../modals/invoicePaymentReminderModal';
import useCurrency from '../../../hooks/useCurrency';
import { isValid } from '../../../helpers/validationHelper';
import TimePeriods from '../../../constants/timePeriods';
import TimeUnits from '../../../constants/timeUnits';
import { 
  getPaymentScheduleAmountDescription, 
  getPaymentScheduleDueDescription, 
  getPaymentScheduleDueOption,
  getSortedPaymentReminders,
  getSortedPaymentSchedules
} from '../../../helpers/invoiceHelper';
import { MdArrowForwardIos, MdOutlineClose, MdOutlineEdit } from "react-icons/md";
import { getNumericCurrency, formatCurrencyString } from '../../../helpers/contractHelper';
import { 
  renderCurrencyField,
  renderFormLabel,
  renderPercentageField
} from '../../../components/formFields'

const InvoiceTemplatePage = () => {

  const [isLoading, setLoading] = useState(true);
  const [invoiceTemplate, setInvoiceTemplate] = useState({});
  const [isEditingTitle, setEditingTitle] = useState(false);
  const [templateName, setTemplateName] = useState("");
  const [notes, setNotes] = useState("");
  const [isTipsActive, setTipsActive] = useState(false);
  const [isPaymentRemindersActive, setPaymentRemindersActive] = useState(false);
  const [isPaymentScheduleActive, setPaymentScheduleActive] = useState(false);
  const [paymentSchedulePayments, setPaymentSchedulePayments] = useState([]);
  const [isZeroAmountModalVisible, setZeroAmountModalVisible] = useState(false);
  const [isPaymentReminderModalVisible, setPaymentReminderModalVisible] = useState(false);
  const [isPaymentScheduleModalVisible, setPaymentScheduleModalVisible] = useState(false);
  const [isFinalPaymentScheduleModalVisible, setFinalPaymentScheduleModalVisible] = useState(false);
  const [paymentReminders, setPaymentReminders] = useState([]);
  const [selectedReminder, setSelectedReminder] = useState({});
  const [selectedPaymentSchedule, setSelectedPaymentSchedule] = useState({});
  const [reminderError, setReminderError] = useState("");
  const [paymentScheduleError, setPaymentScheduleError] = useState("");
  const [isPaymentReminderAdd, setPaymentReminderAdd] = useState(true);
  const [isPaymentScheduleAdd, setPaymentScheduleAdd] = useState(true);
  const [selectedPaymentDueOption, setSelectedPaymentDueOption] = useState("ISSUE_DATE");
  const [selectedDueDateOption, setSelectedDueDateOption] = useState("ISSUE_DATE");
  const [selectedDueDateCalculation, setSelectedDueDateCalculation] = useState({});

  const navigate = useNavigate();
  const params = useParams();
  const inputRef = useRef(null);
  const { useBreakpoint } = Grid;
  const screens = useBreakpoint();
  const [currency] = useCurrency()

  const [paymentScheduleForm] = Form.useForm();
  const [sendRequest] = useApi()

  const templateId = params.id;

  useEffect(() => {
    window.scrollTo(0, 0);
    refreshPage()
  }, []);

  const refreshPage = async () => {
    try {
      const results = await sendRequest(getInvoiceTemplate(templateId));
      setInvoiceTemplate(results)
      setTemplateName(results.invoice_template_name)
      setNotes(results.notes)
      setTipsActive(results.tips ?? false)
      
      var defaultDueDateCalculation = {}
      if (isValid(results.due_date_calculation)) {
        defaultDueDateCalculation = results.due_date_calculation
      } else {
        defaultDueDateCalculation = { time_value: 0, time_unit: TimeUnits.DAYS, time_period: TimePeriods.AFTER_ISSUE_DATE }
      }
      setSelectedDueDateCalculation(defaultDueDateCalculation)
      setSelectedDueDateOption(getPaymentScheduleDueOption(defaultDueDateCalculation))

      // Reminders
      const reminders = isArray(results.payment_reminders) ? results.payment_reminders : []
      if (reminders.length > 0) {
        setPaymentRemindersActive(true)
        setPaymentReminders(reminders)
      }

      // Scheduled payments
      setPaymentScheduleActive(results.has_payment_schedule)
      const payments = isArray(results.scheduled_payments) ? results.scheduled_payments : []
      setPaymentSchedulePayments(payments)

      setLoading(false)
    } catch (error) {
      setLoading(false)
    }
  }

  const onPaymentReminderToggle = (value) => {
    setPaymentRemindersActive(value)
    if (value && paymentReminders.length == 0) {
      const defaultReminders = [
        { id: uniqid(), time_value: 3, time_unit: TimeUnits.DAYS, time_period: TimePeriods.BEFORE },
        { id: uniqid(), time_value: 0, time_unit: TimeUnits.DAYS, time_period: TimePeriods.BEFORE},
        { id: uniqid(), time_value: 3, time_unit: TimeUnits.DAYS, time_period: TimePeriods.AFTER }
      ]
      setPaymentReminders(defaultReminders)
    }
  }

  const onPaymentScheduleToggle = (value) => {
    setPaymentScheduleActive(value)
    if (value && paymentSchedulePayments.length == 0) {
      const defaultPayments = [
        { 
          scheduled_payment_id: uniqid(), 
          scheduled_amount_type: "PERCENTAGE",
          scheduled_amount: 50,
          due_date_calculation: {
            time_value: 0, 
            time_unit: TimeUnits.DAYS, 
            time_period: TimePeriods.AFTER_ISSUE_DATE
          }
        }
      ]
      setPaymentSchedulePayments(defaultPayments)
      const defaultDueDateCalculation = { time_value: 0, time_unit: TimeUnits.DAYS, time_period: TimePeriods.BEFORE_EVENT_DATE }
      setSelectedDueDateOption(getPaymentScheduleDueOption(defaultDueDateCalculation))
      setSelectedDueDateCalculation(defaultDueDateCalculation)
    }
  }

  const onAddPaymentSchedule = () => {
    setPaymentScheduleAdd(true)
    setPaymentScheduleError("")
    setSelectedPaymentDueOption("CUSTOM")
    const dueDateCalculation = {
      time_value: 0, 
      time_unit: TimeUnits.DAYS,
      time_period: TimePeriods.AFTER_ISSUE_DATE
    }
    const defaultPayment = {
      scheduled_payment_id: uniqid(),
      scheduled_amount_type: "AMOUNT",
      scheduled_amount: null,
      due_date_calculation: dueDateCalculation
    }
    setSelectedPaymentSchedule(defaultPayment)
    const data = {
      amount: "",
      percentage: ""
    }
    paymentScheduleForm.setFieldsValue(data)
    setPaymentScheduleModalVisible(true)
  }

  const onEditPaymentSchedule = (payment) => {
    setPaymentScheduleAdd(false)
    setPaymentScheduleError("")
    setSelectedPaymentSchedule(payment)
    setSelectedPaymentDueOption(getPaymentScheduleDueOption(payment))
    const data = {
      amount: payment.scheduled_amount_type == "AMOUNT" ? payment.scheduled_amount : "",
      percentage: payment.scheduled_amount_type == "PERCENTAGE" ? payment.scheduled_amount : ""
    }
    paymentScheduleForm.setFieldsValue(data)
    setPaymentScheduleModalVisible(true)
  }

  const onEditFinalPaymentSchedule = () => {
    setSelectedDueDateOption(getPaymentScheduleDueOption(selectedDueDateCalculation))
    setFinalPaymentScheduleModalVisible(true)
  }

  const onRemovePaymentSchedule = (payment) => {
    var clonedPayments = cloneDeep(paymentSchedulePayments)
    clonedPayments = clonedPayments.filter(x => x.id != payment.id)
    setPaymentSchedulePayments(clonedPayments)
  }

  const submitPaymentSchedule = (values) => {
    var clonedPayments = cloneDeep(paymentSchedulePayments)

    // Validate the form
    if (selectedPaymentDueOption == "CUSTOM" && (isNull(selectedPaymentSchedule.time_value) || selectedPaymentSchedule.time_value == "")) {
      setPaymentScheduleError("Please enter a number before continuing.")
      return
    }

    // Check if payment has already been added
    const foundSameItem = clonedPayments.find(x => {
      return x.time_value == selectedPaymentSchedule.time_value && x.time_unit == selectedPaymentSchedule.time_unit && x.time_period == selectedPaymentSchedule.time_period && x.id != selectedPaymentSchedule.id
    })
    if (!isEmpty(foundSameItem)) {
      setPaymentScheduleError("A payment already exists on this day. Please select another day.")
      return
    }

    // Create new payment object for saving
    const amount = selectedPaymentSchedule.scheduled_amount_type == "AMOUNT" ? getNumericCurrency(values["amount"]) : values["percentage"]
    var timeValue = Number(selectedPaymentSchedule.time_value)
    var timeUnit = selectedPaymentSchedule.time_unit
    var timePeriod = selectedPaymentSchedule.time_period
    if (selectedPaymentDueOption == "EVENT_DATE") {
      timeValue = 0
      timeUnit = TimeUnits.DAYS
      timePeriod = TimePeriods.BEFORE_EVENT_DATE
    } else if (selectedPaymentDueOption == "ISSUE_DATE") {
      timeValue = 0
      timeUnit = TimeUnits.DAYS
      timePeriod = TimePeriods.AFTER_ISSUE_DATE
    }
    const dueDateCalculation = {
      time_value: timeValue,
      time_unit: timeUnit,
      time_period: timePeriod
    }
    const newPayment = {
      scheduled_payment_id: selectedPaymentSchedule.scheduled_payment_id,
      scheduled_amount_type: selectedPaymentSchedule.scheduled_amount_type,
      scheduled_amount: amount,
      due_date_calculation: dueDateCalculation
    }

    // If it's an edit, remove the payment entirely before adding it back
    if (!isPaymentScheduleAdd) {
      clonedPayments = clonedPayments.filter(x => x.id != newPayment.id)
    }

    clonedPayments.push(newPayment)
    setPaymentSchedulePayments(clonedPayments)
    setPaymentScheduleModalVisible(false)
  }

  const submitFinalPaymentSchedule = () => {
    setFinalPaymentScheduleModalVisible(false)
  }

  const onDueDateOptionChange = (value) => {
    setSelectedDueDateOption(value)
    if (value == "EVENT_DATE") {
      setSelectedDueDateCalculation({ time_value: 0, time_unit: TimeUnits.DAYS, time_period: TimePeriods.BEFORE_EVENT_DATE })
    } else if (value == "ISSUE_DATE") {
      setSelectedDueDateCalculation({ time_value: 0, time_unit: TimeUnits.DAYS, time_period: TimePeriods.AFTER_ISSUE_DATE })
    }
  }

  const startEditingTitle = () => {
    setEditingTitle(true)
    setTimeout(() => {
      inputRef.current.focus()
    }, 100)
  }

  const saveName = async () => {
    setEditingTitle(false)
  }

  const onKeyPress = (e) => {
    if (e.keyCode == 13) {
      saveName()
    }
  }

  const onSave = async () => {

    const finalScheduledPayments = isPaymentScheduleActive ? paymentSchedulePayments : []
    const finalPaymentReminders = isPaymentRemindersActive ? paymentReminders : []

    try {
      const body = {
        invoice_template_name: templateName,
        due_date_calculation: selectedDueDateCalculation,
        notes: notes,
        has_payment_schedule: isPaymentScheduleActive,
        scheduled_payments: finalScheduledPayments,
        tips: isTipsActive,
        payment_reminders: finalPaymentReminders
      }

      await sendRequest(updateIvoiceTemplate(invoiceTemplate.invoice_template_id, body))
      navigate(`/admin/invoice-templates`)
    } catch {
      notification.error({
        message: 'Error',
        description: 'Something went wrong.',
        duration: 3
      });
    }
  }

  const onAddReminder = () => {
    const defaultPaymentReminder = { id: uniqid(), time_value: "", time_unit: TimeUnits.DAYS, time_period: TimePeriods.BEFORE }
    setSelectedReminder(defaultPaymentReminder)
    setPaymentReminderAdd(true)
    setReminderError("")
    setPaymentReminderModalVisible(true)
  }

  const onEditReminder = (reminder) => {
    setSelectedReminder(reminder)
    setPaymentReminderAdd(false)
    setReminderError("")
    setPaymentReminderModalVisible(true)
  }

  const onRemoveReminder = (id) => {
    var clonedReminders = cloneDeep(paymentReminders)
    clonedReminders = clonedReminders.filter(x => x.id != id)
    setPaymentReminders(clonedReminders)
  }

  const submitPaymentReminder = () => {
    var clonedReminders = cloneDeep(paymentReminders)
    
    // Validate the form
    if (isNull(selectedReminder.time_value) || selectedReminder.time_value == "") {
      setReminderError("Please enter a number before continuing.")
      return
    }

    // Check if reminder has already been added
    const foundSameReminder = clonedReminders.find(x => {
      return x.time_value == selectedReminder.time_value && x.time_unit == selectedReminder.time_unit && x.time_period == selectedReminder.time_period && x.id != selectedReminder.id
    })
    if (!isEmpty(foundSameReminder)) {
      setReminderError("A reminder already exists on this day. Please try again.")
      return
    }

    // If it's an edit, remove the reminder entirely before adding it back
    if (!isPaymentReminderAdd) {
      clonedReminders = clonedReminders.filter(x => x.id != selectedReminder.id)
    }

    const newReminder = {
      ...selectedReminder,
      time_value: Number(selectedReminder.time_value)
    }

    clonedReminders.push(newReminder)
    setPaymentReminders(clonedReminders)
    setPaymentReminderModalVisible(false)
  }

  const renderHeader = () => {
    return (
      <div className="p-20">
        <div>
          <span className="c-blue fw-700 cursor-default" onClick={() => navigate("/admin/invoice-templates")}>
            Invoice Templates
          </span>
          <span className="fs-10 mh-5"><MdArrowForwardIos /></span>
          <span className="cursor-default c-text-gray">
            Edit
          </span>
        </div>
        <Row gutter={[15,15]}>
          <Col xs={18}>
            <div className={`header-editable-title mt-5 ${isEditingTitle ? "editing" : ""}`}>
              <div className="header-editable-title--label" onClick={() => startEditingTitle()}>{templateName}</div>
              <div className="header-editable-title--input">
                <Input
                  size="large"
                  placeholder="Template Name"
                  ref={inputRef}
                  onBlur={() => saveName()}
                  value={templateName}
                  onChange={(e) => setTemplateName(e.target.value)}
                  onKeyUp={onKeyPress}
                />
              </div>
            </div>
          </Col>
          <Col xs={6} className="text-right">
            <button className="page-title-button" onClick={onSave}>Save</button>
          </Col>
        </Row>
      </div>
    )
  }

  const renderPaymentScheduleActions = (payment) => {
    return (
      <div className="display-flex" style={{ justifyContent: 'flex-end'}}>
        <div className="display-flex ph-5" onClick={() => onEditPaymentSchedule(payment)}>
          <MdOutlineEdit size={20} color={"#CCC"}/>
        </div>
        { paymentSchedulePayments.length > 1 && (
          <div className="display-flex ph-5" onClick={() => onRemovePaymentSchedule(payment)}>
            <MdOutlineClose size={24} color={"#CCC"}/>
          </div>
        )}
      </div>
    )
  }

  const renderFinalPaymentScheduleActions = () => {
    return (
      <div className="display-flex" style={{ justifyContent: 'flex-end'}}>
        <div className="display-flex ph-5" onClick={() => onEditFinalPaymentSchedule()}>
          <MdOutlineEdit size={20} color={"#CCC"}/>
        </div>
      </div>
    )
  }

  const renderPaymentScheduleRow = (item, index) => {
    return (
      <Row gutter={[15,5]} className="bg-gray ph-10 pv-15 mt-10">
        <Col xs={12} md={4} className="c-text-gray">
          { index + 1 } of { paymentSchedulePayments.length + 1 }
        </Col>
        { !screens.md && (
          <Col xs={12}>
            { renderPaymentScheduleActions(item) }
          </Col>
        )}
        <Col xs={24} md={6} className="pl-10">
          { getPaymentScheduleAmountDescription(item, currency.code) }
        </Col>
        <Col xs={24} md={10} className="pl-10">
          { getPaymentScheduleDueDescription(item.due_date_calculation) }
        </Col>
        { screens.md && (
          <Col xs={4}>
            { renderPaymentScheduleActions(item) }
          </Col>
        )}
      </Row>
    )
  }

  const renderFinalPaymentScheduleRow = () => {
    const totalPaymentSchedules = paymentSchedulePayments.length + 1
    return (
      <Row className="bg-gray p-10 mt-10" gutter={[20,20]} align="middle">
        <Col xs={24} md={4} className="c-text-gray">
          {totalPaymentSchedules} of {totalPaymentSchedules}
        </Col>
        { !screens.md && (
          <Col xs={12}>
            { renderFinalPaymentScheduleActions() }
          </Col>
        )}
        <Col xs={24} md={6}>
          <div className="">Balance</div>
          <div className="c-text-gray fs-10">(Automatically calculated)</div>
        </Col>
        <Col xs={24} md={10}>
          { getPaymentScheduleDueDescription(selectedDueDateCalculation) }
        </Col>
        { screens.md && (
          <Col xs={4}>
            { renderFinalPaymentScheduleActions() }
          </Col>
        )}
      </Row>
    )
  }

  const renderPaymentSchedule = () => {
    return (
      <>
        { screens.md ? (
          <Row gutter={[15,15]} className="b-border mt-20 pb-10 c-text-gray fs-12 fw-700 pl-10">
            <Col xs={4}>
              PAYMENT
            </Col>
            <Col xs={6} className="pl-10">
              AMOUNT
            </Col>
            <Col xs={14} className="pl-10">
              DUE
            </Col>
          </Row>
        ) : (
          <Row gutter={[15,15]} className="b-border mt-20 pb-10 c-text-gray fs-12 fw-700 pl-10">
            <Col xs={24}>
              PAYMENT / AMOUNT / DUE
            </Col>
          </Row>
        )}
        { getSortedPaymentSchedules(paymentSchedulePayments).map((x,i) => renderPaymentScheduleRow(x,i))}
        { renderFinalPaymentScheduleRow() }
        { paymentSchedulePayments.length < 5 && (
          <div className="blue-link mt-15" onClick={() => onAddPaymentSchedule()}>+ Add Payment</div>
        )}
      </>
    )
  }

  const renderZeroAmountModal = () => {
    return (
      <Modal visible={isZeroAmountModalVisible} closable={false} footer={null} width={400} wrapClassName="rounded-modal">
        <div className="text-right" onClick={() => setZeroAmountModalVisible(false)}><MdOutlineClose size={30} color={"#CCC"}/></div>
        <div className="mt-10">
          <div className="fw-700 fs-20 text-center">Invalid Amounts</div>
          <div className="fw-500 fs-14 mt-20 mb-20 text-center">All amounts in the payment schedule must be greater than {formatCurrencyString(0, currency.code)}.</div>
          <button className="primary-button" type="button" onClick={() => setZeroAmountModalVisible(false)}>OK</button>
        </div>
      </Modal>
    )
  }

  const renderPaymentReminderActions = (reminder) => {
    return (
      <div className="display-flex" style={{ justifyContent: 'flex-end'}}>
        <div className="display-flex ph-5" onClick={() => onEditReminder(reminder)}>
          <MdOutlineEdit size={20} color={"#CCC"}/>
        </div>
        { paymentReminders.length > 1 && (
          <div className="display-flex ph-5" onClick={() => onRemoveReminder(reminder.id)}>
            <MdOutlineClose size={24} color={"#CCC"}/>
          </div>
        )}
      </div>
    )
  }

  const renderPaymentReminder = (reminder, index) => {
    var reminderDescription = `${ reminder.time_value } ${ toLower(reminder.time_unit) } ${ toLower(reminder.time_period) } due date`
    if (reminder.time_value == 0) {
      reminderDescription = "On due date"
    }

    return (
      <Row gutter={[15,5]} className="bg-gray ph-10 pv-15 mt-10">
        <Col xs={12} md={4} className="c-text-gray">
          { index + 1 } of { paymentReminders.length }
        </Col>
        { !screens.md && (
          <Col xs={12}>
            { renderPaymentReminderActions(reminder) }
          </Col>
        )}
        <Col xs={16} className="pl-10">
          { reminderDescription }
        </Col>
        { screens.md && (
          <Col xs={4}>
            { renderPaymentReminderActions(reminder) }
          </Col>
        )}
      </Row>
    )
  }

  const renderPaymentReminders = () => {
    return (
      <div>
        <Row gutter={[15,15]} className="b-border mt-20 pb-10 c-text-gray fs-12 fw-700 pl-10">
          <Col xs={4}>
            REMINDER
          </Col>
          <Col xs={20} className="pl-10">
            WHEN?
          </Col>
        </Row>
        { getSortedPaymentReminders(paymentReminders).map((x,i) => renderPaymentReminder(x,i)) }
        { paymentReminders.length <= 10 && (
          <div className="blue-link mt-10" onClick={onAddReminder}>+ Add Reminder</div>
        )}
      </div>
    )
  }

  const renderPaymentReminderModal = () => {
    return (
      <InvoicePaymentReminderModal 
        isVisible={isPaymentReminderModalVisible}
        setVisible={setPaymentReminderModalVisible}
        title={isPaymentReminderAdd ? "Add Payment Reminder" : "Edit Payment Reminder" } 
        selectedReminder={selectedReminder}
        setSelectedReminder={setSelectedReminder}
        submit={submitPaymentReminder}
        reminderError={reminderError}
      />
    )
  }
  
  const renderPaymentScheduleModal = () => {
    return (
      <Modal visible={isPaymentScheduleModalVisible} footer={null} onCancel={() => setPaymentScheduleModalVisible(false)} width={500} closable={false} maskClosable={false} wrapClassName="rounded-modal">
        <Form form={paymentScheduleForm} layout="vertical" name="edit-package" onFinish={submitPaymentSchedule}>
          <Row align="middle">
            <Col flex={1}>
              <div className="fw-700 fs-18">{isPaymentScheduleAdd ? "Add" : "Edit" } Payment</div>
            </Col>
            <Col>
              <div className="display-flex" onClick={() => setPaymentScheduleModalVisible(false)}><MdOutlineClose size={30} color={"#CCC"}/></div>
            </Col>
          </Row>
          <Row className="mt-20" gutter={[15, 15]}>
            <Col xs={24}>
              { renderFormLabel("Type")}
              <Radio.Group value={selectedPaymentSchedule.scheduled_amount_type} onChange={(e) => setSelectedPaymentSchedule({ ...selectedPaymentSchedule, scheduled_amount_type: e.target.value})}>
                <Space direction="vertical">
                  <Radio value="AMOUNT">Amount</Radio>
                  <Radio value="PERCENTAGE">Percentage</Radio>
                </Space>
              </Radio.Group>
            </Col>
            { selectedPaymentSchedule.scheduled_amount_type == "AMOUNT" && (
              <Col xs={24}>
                { renderCurrencyField("Amount", "amount", true, currency.code) }
              </Col>
            )}
            { selectedPaymentSchedule.scheduled_amount_type == "PERCENTAGE" && (
              <Col xs={24}>
                { renderPercentageField("Percentage", "percentage", true) }
              </Col>
            )}
            <Col xs={24}>
              { renderFormLabel("When?")}
              <Row>
                <Col xs={24}>
                  <Select
                    style={{ width: '100%' }}
                    placeholder={"Select an option..."}
                    size='large'
                    value={selectedPaymentDueOption}
                    onChange={(value) => setSelectedPaymentDueOption(value)}
                  >
                    <Select.Option value="ISSUE_DATE">On issue date</Select.Option>
                    <Select.Option value="EVENT_DATE">On event date</Select.Option>
                    <Select.Option value="CUSTOM">Custom</Select.Option>
                  </Select>
                </Col>
              </Row>
              { selectedPaymentDueOption == "CUSTOM" &&  (
                <InvoicePaymentTimePicker timeObject={selectedPaymentSchedule} setTimeObject={setSelectedPaymentSchedule}/>
              )}
            </Col>
          </Row>
          <Row gutter={[10,10]} className="mt-20">
            <Col xs={24}>
              <div className="fs-12 c-red text-center">{ paymentScheduleError }</div>
            </Col>
            <Col xs={24}>
              <div className="mt-20 text-right">
                <Button className="admin-small-button secondary" onClick={() => setPaymentScheduleModalVisible(false)}>Cancel</Button>
                <Button className="admin-small-button ml-10" htmlType="submit">Save</Button>
              </div>
            </Col>
          </Row>
        </Form>
      </Modal>
    )
  }

  const renderFinalPaymentScheduleModal = () => {
    return (
      <Modal visible={isFinalPaymentScheduleModalVisible} footer={null} onCancel={() => setFinalPaymentScheduleModalVisible(false)} width={500} closable={false} maskClosable={false} wrapClassName="rounded-modal">
        <Row align="middle">
          <Col flex={1}>
            <div className="fw-700 fs-18">Edit Payment</div>
          </Col>
          <Col>
            <div className="display-flex" onClick={() => setFinalPaymentScheduleModalVisible(false)}><MdOutlineClose size={30} color={"#CCC"}/></div>
          </Col>
        </Row>
        <Row className="mt-20" gutter={[15, 15]}>
          <Col xs={24}>
            { renderFormLabel("Amount")}
            <div className="bg-gray p-10">
              Since this is the final payment, the balance will be automatically calculated.
            </div>
          </Col>
          <Col xs={24}>
            { renderFormLabel("When?")}
            { renderDueDateOption() }
          </Col>
        </Row>
        <Row gutter={[10,10]} className="mt-20">
          <Col xs={24}>
            <div className="fs-12 c-red text-center">{ paymentScheduleError }</div>
          </Col>
          <Col xs={24}>
            <div className="mt-20 text-right">
              <Button className="admin-small-button secondary" onClick={() => setFinalPaymentScheduleModalVisible(false)}>Cancel</Button>
              <Button className="admin-small-button ml-10" onClick={submitFinalPaymentSchedule}>Save</Button>
            </div>
          </Col>
        </Row>
      </Modal>
    )
  }
  
  const renderDueDateOption = () => {
    return (
      <>
        <Row align="middle">
          <Col xs={24}>
            <Select
              style={{ width: '100%' }}
              placeholder={"Select an option..."}
              size='large'
              value={selectedDueDateOption}
              onChange={(value) => onDueDateOptionChange(value)}
            >
              <Select.Option value="ISSUE_DATE">On issue date</Select.Option>
              <Select.Option value="EVENT_DATE">On event date</Select.Option>
              <Select.Option value="CUSTOM">Custom</Select.Option>
            </Select>
          </Col>
        </Row>
        <Row align="middle">
          <Col xs={24}>
            { selectedDueDateOption == "CUSTOM" &&  (
              <InvoicePaymentTimePicker timeObject={selectedDueDateCalculation} setTimeObject={setSelectedDueDateCalculation}/>
            )}
          </Col>
        </Row>
      </>
    )
  }

  const renderContent = () => {
    const paddingClass = screens.md ? "p-30" : "p-20"
    return (
      <FloatingContainer className="ph-20 mb-80" verticalPadding={20} maxWidth={800}>
        <div className="fs-20 fw-700">Invoice Template</div>
        <div className="c-text-gray mt-5 mb-15">Set default values below for an invoice. This template will display as an option when creating a new invoice.</div>
        <div className={`shadow-card-square ${paddingClass} mt-15`}>
          <div className="fs-20 fw-700 mb-10">Notes</div>
          <Row className="mt-10" gutter={[20, 20]}>
            <Col xs={24} md={24}>
              <Input.TextArea rows={4} placeholder={"Add optional notes, terms, etc."} value={notes} onChange={(e) => setNotes(e.target.value)} />
            </Col>
          </Row>
        </div>

        <div className={`shadow-card-square ${paddingClass} mt-15`}>
          <div className="fs-20 fw-700">Tips</div>
          <div className="c-text-gray mt-5 mb-15">Give your clients the option to leave you a tip.</div>
          <Switch checked={isTipsActive} onChange={(value) => setTipsActive(value)} /> <span className="ml-10 fw-600">{isTipsActive ? "On" : "Off"}</span>

          <div className="fs-20 fw-700 mt-30 pt-30 t-border">Payment Reminders</div>
          <div className="c-text-gray mt-5 mb-15">Send automatic reminders to your clients when each payment is due.</div>
          <Switch checked={isPaymentRemindersActive} onChange={(value) => onPaymentReminderToggle(value)} /> <span className="ml-10 fw-600">{isPaymentRemindersActive ? "On" : "Off"}</span>
          { isPaymentRemindersActive && renderPaymentReminders() }
        </div>

        <div className={`shadow-card-square ${paddingClass} mt-15`}>
          <Row className="" gutter={[20, 15]}>
            <Col xs={24} md={24}>
              <div className="fs-20 fw-700 mb-5">Payment Schedule</div>
              <div className="mb-5 c-text-gray">Use a payment schedule to collect deposits/retainers or setup installments.</div>
            </Col>
            <Col xs={24} md={24}>
              <Switch checked={isPaymentScheduleActive} onChange={(value) => onPaymentScheduleToggle(value)} /> <span className="ml-10 fw-600">{isPaymentScheduleActive ? "On" : "Off"}</span>
            </Col>
            {isPaymentScheduleActive && (
              <Col xs={24} md={24}>
                { renderPaymentSchedule() }
              </Col>
            )}
          </Row>

          {!isPaymentScheduleActive && (
            <>
              <div className="fs-20 fw-700 mt-30 pt-30 t-border">Due Date</div>
              <div className="c-text-gray mt-5 mb-15">The date the final payment is due.</div>
              <Row>
                <Col xs={24} sm={12}>
                  { renderDueDateOption() }
                </Col>
              </Row>
            </>
          )}
        </div>
        { renderZeroAmountModal() }
        { renderPaymentReminderModal() }
        { renderPaymentScheduleModal() }
        { renderFinalPaymentScheduleModal() }
      </FloatingContainer>
    )
  }

  if (isLoading) {
    return <LoadingSpinner />
  }

  return (
    <AdminContent header={renderHeader()} body={renderContent()} />
  );
}

export default InvoiceTemplatePage;